import React, { useState, useEffect } from 'react';
import {
    Box, Grid, Typography, Paper, Button, IconButton, Divider, TextField,
    Avatar
} from '@mui/material';
import validationAuth from '../../validationAuth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import EditIcon from '@mui/icons-material/Edit';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { useIsLoginState } from '../../components/IsLoginContext';
import * as common from '../../commons/common';
import { styled } from '@mui/material/styles';
import AlertForConfirm from '../../components/alertForConfirm';

function PersonalInfo() {
    const navigate = useNavigate();
    const { user, setIsLogin } = useIsLoginState();
    const [userData, setUserData] = useState();
    const [isLogoutAlertOpen, setIsLogoutAlertOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [isSaveAlertOpen, setIsSaveAlertOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const fileInputRef = React.useRef();
    const [phoneError, setPhoneError] = useState("");

    // 사용자 정보 가져오기
    useEffect(() => {
        const fetchUserData = async () => {
            const memberId = localStorage.getItem('memberId');
            if (memberId) {
                try {
                    const url = `${common.getApiUrl()}/member/${memberId}`;
                    const accessToken = localStorage.getItem('access_token');
                    const response = await axios.get(url, {
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    });
                    if (response.data) {
                        setUserData(response.data);
                        setEditedData(response.data);
                    }
                } catch (error) {
                    alert('사용자 정보를 불러올 수 없습니다.')
                    navigate('/')
                }
            }
        };

        fetchUserData();
    }, []);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleSaveClick = () => {
        // 전화번호 유효성 검사 추가
        if (editedData.phone) {
            if (!/^\d{11}$/.test(editedData.phone)) {
                setPhoneError("숫자 11자리를 입력해주세요.");
                return; // 유효성 검사 실패 시 저장 진행하지 않음
            }
        }

        setIsSaveAlertOpen(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedData(userData);
        setSelectedImage(null);
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;

        // 휴대폰 번호 유효성 검사
        if (field === 'phone') {
            // 숫자만 입력 가능하도록 처리
            if (!/^\d*$/.test(value)) {
                setPhoneError("숫자만 입력해주세요.");
                return; // 숫자가 아닌 문자 입력 시 상태 업데이트하지 않음
            }

            // 11자리 확인
            if (value.length > 0 && value.length !== 11) {
                setPhoneError("숫자 11자리를 입력해주세요.");
            } else {
                setPhoneError("");
            }
        }

        setEditedData({
            ...editedData,
            [field]: value
        });
    };

    const handleImageClick = () => {
        if (isEditing) {
            fileInputRef.current.click();
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(URL.createObjectURL(file));
            // 이미지 파일을 editedData에 추가
            setEditedData({
                ...editedData,
                profileImage: file
            });
        }
    };

    const handleSaveConfirm = async () => {
        setIsSaveAlertOpen(false);
        const accessToken = localStorage.getItem('access_token');

        try {
            const url = `${common.getApiUrl()}/member`;
            const modifiedMemeberRequest = {
                name: editedData.memberNm,
                phone: editedData.phone,
                email: editedData.email,
                // 현재 이미지 ID가 있다면 유지
                imageId: userData.imageId || null
            };

            // 항상 FormData 형식으로 전송
            const formData = new FormData();
            formData.append('modifiedMemeberRequest', new Blob([JSON.stringify(modifiedMemeberRequest)], {
                type: 'application/json'
            }));

            // 새 이미지가 선택된 경우에만 파일 추가
            if (editedData.profileImage) {
                formData.append('uploadFile', editedData.profileImage);
            }

            // FormData로 전송
            await axios.put(url, formData, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            // 업데이트 후 사용자 정보 다시 가져오기
            const memberId = localStorage.getItem('memberId');
            const refreshUrl = `${common.getApiUrl()}/member/${memberId}`;
            const refreshResponse = await axios.get(refreshUrl, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (refreshResponse.data) {
                setUserData(refreshResponse.data);
                setEditedData(refreshResponse.data);
            }

            setIsEditing(false);
            alert('정보가 성공적으로 수정되었습니다.');
        } catch (error) {
            alert('정보 수정 중 오류가 발생했습니다.');
            console.error('Update error:', error);
        }
    };

    const handleLogoutClick = () => {
        setIsLogoutAlertOpen(true);
    };

    const handleClose = () => {
        setIsLogoutAlertOpen(false);
        setIsSaveAlertOpen(false);
    };

    const handleLogout = async () => {
        setIsLogoutAlertOpen(false);
        const url = `${common.getApiUrl()}/auth/logout`;
        const accessToken = localStorage.getItem('access_token');

        try {
            await axios.post(url, {}, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setIsLogin(false);
            navigate('/', { replace: true });
        } catch (error) {
            common.handleApiError(error);
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    if (!userData) {
        return <div>Loading...</div>;
    }

    const StyledTypography = styled(Typography)(({ theme }) => ({
        fontSize: '1rem',
        fontWeight: 'bold',
    }));

    const StyledDivider = styled(Divider)(({ theme }) => ({
        marginY: theme.spacing(1),
        borderBottomWidth: 1,
    }));

    const renderField = (label, value, field) => (
        <>
            <Grid container spacing={2} alignItems="center" sx={{ p: 1 }}>
                <Grid item xs={3}>
                    <StyledTypography variant="body1" sx={{ fontWeight: 'normal' }}>{label}</StyledTypography>
                </Grid>
                <Grid item xs={9}>
                    {isEditing && ['memberNm', 'phone', 'email'].includes(field) ? (
                        <Box>
                            <TextField
                                fullWidth
                                value={editedData[field] || ''}
                                onChange={handleInputChange(field)}
                                variant="outlined"
                                size="small"
                                error={field === 'phone' && !!phoneError}
                            />
                            {field === 'phone' && phoneError && (
                                <Typography color="error" variant="caption">
                                    {phoneError}
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <StyledTypography variant="body1">{value}</StyledTypography>
                    )}
                </Grid>
            </Grid>
            <StyledDivider />
        </>
    );

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            backgroundColor: 'var(--main-white-color)',
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                padding: '10px 20px',
                backgroundColor: 'var(--sub-darkblue-color)',
                color: 'white',
                minHeight: 65,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleGoBack}>
                    <IconButton sx={{ color: 'white', padding: '1px' }}>
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontSize: '1.2rem' }}></Typography>
                </Box>
                {!isEditing && (
                    <IconButton onClick={handleEditClick} sx={{ color: 'white' }}>
                        <EditIcon />
                    </IconButton>
                )}
            </Box>

            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                width: '100%',
                backgroundColor: 'var(--main-blue-color)',
                height: '130px',
                minHeight: '100px',
            }}>
                <Box
                    sx={{
                        position: 'relative',
                        cursor: isEditing ? 'pointer' : 'default',
                    }}
                    onClick={handleImageClick}
                >
                    <Avatar
                        sx={{
                            width: 70,
                            height: 70,
                            marginBottom: '10px',
                            backgroundColor: 'var(--main-white-color)',
                        }}
                        src={selectedImage || (userData.imgSrc ? `${common.getImageBaseUrl()}${userData.imgSrc}` : userData.profileImage)}
                    />
                    {isEditing && (
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 10,
                                right: -10,
                                backgroundColor: 'white',
                                borderRadius: '50%',
                                padding: '4px',
                            }}
                        >
                            <CameraAltIcon sx={{ fontSize: 20, color: 'var(--main-blue-color)' }} />
                        </Box>
                    )}
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageChange}
                        accept="image/*"
                        style={{ display: 'none' }}
                    />
                </Box>
                <Typography variant="body2" sx={{
                    fontSize: '1.0rem',
                    color: 'white',
                    textAlign: 'center',
                }}>
                    {editedData.memberNm} 님
                </Typography>
            </Box>

            <Box sx={{ p: 2, flexGrow: 1 }}>
                <Paper sx={{ p: 2, mb: 2, boxShadow: 'none', backgroundColor: 'var(--main-white-color)' }}>
                    {renderField('이름', userData.memberNm, 'memberNm')}
                    {renderField('휴대폰', userData.phone, 'phone')}
                    {renderField('이메일', userData.email, 'email')}
                    {renderField('소속',
                        `${userData.dept[0] ? userData.dept[0].deptNm : ''}
                        ${userData.dept[1] ? ` - ${userData.dept[1].deptNm}` : ''}
                        ${userData.dept[2] ? ` - ${userData.dept[2].deptNm}` : ''}`,
                        'dept'
                    )}
                    {renderField('직위', userData.position, 'position')}
                </Paper>

                <Grid container direction="column" justifyContent="center" alignItems="center" sx={{ mb: 2, mt: 3 }}>
                    {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleSaveClick}
                                sx={{
                                    backgroundColor: 'var(--main-blue-color)',
                                    borderRadius: '50px',
                                    width: '10rem',
                                    height: '2rem',
                                    fontSize: '1rem',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: 'var(--main-blue-color)',
                                        boxShadow: 'none',
                                    }
                                }}
                            >
                                저장
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleCancelEdit}
                                sx={{
                                    borderColor: 'var(--main-blue-color)',
                                    color: 'var(--main-blue-color)',
                                    borderRadius: '50px',
                                    width: '10rem',
                                    height: '2rem',
                                    fontSize: '1rem',
                                    boxShadow: 'none',
                                }}
                            >
                                취소
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleLogoutClick}
                            sx={{
                                backgroundColor: 'var(--main-blue-color)',
                                borderRadius: '50px',
                                width: '10rem',
                                height: '2rem',
                                fontSize: '1rem',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: 'var(--main-blue-color)',
                                    boxShadow: 'none',
                                }
                            }}
                        >
                            로그아웃
                        </Button>
                    )}
                </Grid>
            </Box>

            <AlertForConfirm
                open={isLogoutAlertOpen}
                onClose={handleClose}
                onConfirm={handleLogout}
                contentText="로그아웃 하시겠습니까?"
            />

            <AlertForConfirm
                open={isSaveAlertOpen}
                onClose={handleClose}
                onConfirm={handleSaveConfirm}
                contentText="변경사항을 저장하시겠습니까?"
            />
        </Box>
    );
}

export default validationAuth(PersonalInfo);